/**
 * Mirrors rocketlane-agentbuilder `features/documentation-agents/utils/transformers.ts`
 * (transformApiToPrompt, transformPromptToApiUpdate) so rocket-ai-frontend does not
 * load these from the agentBuilder remote.
 */
import {
  CanCreatePageOn,
  CreateOnEvent,
  SourceSelectionMode,
  type DocumentationAgentPrompt,
  type KnowledgeItem,
} from './document-agent-prompt.types';
import type {
  DocumentationAgent,
  DocumentationAgentConfig,
  DocumentationAgentSource,
} from 'api/document-agent/types';

interface KnowledgeBaseInput {
  type: string;
  id: number;
  name: string;
  path: string;
}

export const transformApiToPrompt = (
  agent: DocumentationAgent,
): DocumentationAgentPrompt => {
  const meetingSource = agent.configuration.sources?.find(
    (source) => source.pageType === 'MEETING',
  );
  const emailSource = agent.configuration.sources?.find(
    (source) => source.pageType === 'EMAIL',
  );

  const meetingTemplateIds =
    meetingSource?.templateIdList?.map((id) => id.toString()) || [];
  const emailTemplateIds =
    emailSource?.templateIdList?.map((id) => id.toString()) || [];

  const sourceSelectionMode =
    agent.configuration.sourceType === 'USER_INPUT'
      ? SourceSelectionMode.UserInput
      : SourceSelectionMode.Templates;

  const knowledge: KnowledgeItem[] =
    agent.configuration.knowledgeBaseConfig?.inputs?.map((input) => {
      const isFolder = input.type === 'FOLDER';

      return {
        id: input.id.toString(),
        name: input.name,
        type: isFolder ? 'folder' : 'document',
        actualContentType: isFolder
          ? 'DEFAULT'
          : (input.type as KnowledgeItem['actualContentType']),
      };
    }) || [];

  return {
    agentId: agent.id,
    agentName: agent.name,
    documentTemplateId: agent.configuration.templateId.toString(),
    sourceSelectionMode,
    includeMeetings: !!meetingSource,
    includeEmails: !!emailSource,
    meetingTemplateIds,
    emailTemplateIds,
    acceptSourcesAtRuntime: agent.configuration.acceptSourcesAtRuntime ?? false,
    canCreatePageOn:
      agent.configuration.creationTrigger === 'AUTOMATION'
        ? CanCreatePageOn.OnEvent
        : CanCreatePageOn.Manual,
    createOnEvent:
      agent.configuration.creationTrigger === 'AUTOMATION'
        ? CreateOnEvent.OAccountCreate
        : ('' as CreateOnEvent | ''),
    updateDocument: agent.configuration.updateDocument,
    knowledge,
    inputs: Array.isArray(agent.input_fields) ? agent.input_fields : [],
  };
};

export const transformPromptToApiUpdate = (
  prompt: DocumentationAgentPrompt,
  existingAgent: DocumentationAgent,
): Partial<DocumentationAgent> => {
  const sources: DocumentationAgentSource[] = [];
  const isUserInputMode =
    prompt.sourceSelectionMode === SourceSelectionMode.UserInput;

  if (isUserInputMode) {
    if (prompt.includeMeetings) {
      sources.push({
        pageType: 'MEETING',
      });
    }

    if (prompt.includeEmails) {
      sources.push({
        pageType: 'EMAIL',
      });
    }
  } else {
    if (prompt.includeMeetings && prompt.meetingTemplateIds.length > 0) {
      sources.push({
        pageType: 'MEETING',
        templateIdList: prompt.meetingTemplateIds.map((id) => parseInt(id, 10)),
      });
    }

    if (prompt.includeEmails && prompt.emailTemplateIds.length > 0) {
      sources.push({
        pageType: 'EMAIL',
        templateIdList: prompt.emailTemplateIds.map((id) => parseInt(id, 10)),
      });
    }
  }

  const sourceType = isUserInputMode ? 'USER_INPUT' : 'TEMPLATE';

  const knowledgeBaseInputs: KnowledgeBaseInput[] = prompt.knowledge.map(
    (item) => ({
      type:
        item.type === 'folder'
          ? item.actualContentType === 'DEFAULT'
            ? 'FOLDER'
            : item.actualContentType
          : (item.actualContentType as 'FILE' | 'API_DOC' | 'WEBSITE'),
      id: parseInt(item.id, 10),
      name: item.name,
      path: item.name,
    }),
  );

  const configuration: DocumentationAgentConfig = {
    ...existingAgent.configuration,
    templateId: parseInt(prompt.documentTemplateId, 10),
    creationTrigger:
      prompt.canCreatePageOn === CanCreatePageOn.OnEvent
        ? 'AUTOMATION'
        : 'MANUAL',
    updateDocument: prompt.updateDocument,
    sourceType,
    acceptSourcesAtRuntime: prompt.acceptSourcesAtRuntime,
    sources,
    knowledgeBaseConfig:
      prompt?.knowledge?.length > 0
        ? {
            inputs: knowledgeBaseInputs,
          }
        : undefined,
  };

  const { input_fields: _inputFields, ...agentWithoutInputs } = existingAgent;

  return {
    ...agentWithoutInputs,
    name: prompt.agentName,
    configuration,
    input_fields: prompt.inputs || [],
  } as Partial<DocumentationAgent> & {
    input_fields: any;
  };
};
